import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import {
  ArrowClockwise,
  ChevronDown,
  ChevronRight,
} from "react-bootstrap-icons";
import CustomDropdown from "../components/CustomDropdown";
import {
  fetchPaymentHistory,
  type BillingPaymentsFilterParams,
  type SubscriptionPayment,
} from "../api/stripe_billing";
import { createBillingSupportTicket } from "../api/support_tickets";
import "./SubscriptionBilling.css";
import "./SuperAdminPlans-new.css";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "succeeded", label: "Succeeded" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "partially_refunded", label: "Partially Refunded" },
  { value: "refunded", label: "Refunded" },
];
const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "subscription", label: "Subscription" },
  { value: "initial_recharge", label: "Initial Credits" },
  { value: "auto_recharge", label: "Auto-Recharge" },
  { value: "manual_recharge", label: "Manual Recharge" },
  { value: "overage_debt_settlement_recharge", label: "Overage Debt Settlement" },
];
const SUPPORT_TICKET_REASON_OPTIONS = [
  { value: "incorrect_amount", label: "Incorrect amount" },
  { value: "duplicate_charge", label: "Duplicate charge" },
  { value: "service_issue", label: "Service issue" },
  { value: "other", label: "Other" },
];

function formatDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : "-";
}
function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
function formatFor(payment: SubscriptionPayment): string {
  return payment.payment_type === "llm_usage_recharge"
    ? "LLM Usage Credits"
    : payment.plan_name_snapshot || "Subscription plan";
}
function formatType(payment: SubscriptionPayment): string {
  if (payment.row_kind === "refund")
    return payment.status === "refunded"
      ? "Refund of payment above"
      : "Partial refund of payment above";
  if (payment.row_kind === "related") return "Original payment";
  if (payment.payment_type === "llm_usage_recharge")
    return (
      (
        {
          initial_recharge: "Initial Credits",
          auto_recharge: "Auto-Recharge",
          manual_recharge: "Manual Recharge",
          overage_debt_settlement_recharge: "Overage Debt Settlement",
        } as Record<string, string>
      )[payment.recharge_type || ""] || "LLM Usage Credits Recharge"
    );
  const context = payment.payment_context || "";
  return (
    (
      {
        initial: "New subscription",
        renewal: "Renewal",
        upgrade: "Upgrade",
        downgrade: "Downgrade",
      } as Record<string, string>
    )[context] || "Subscription payment"
  );
}
function formatStatus(status: string): string {
  return status === "partially_refunded"
    ? "Partially Refunded"
    : status === "refunded"
      ? "Refunded"
      : status
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());
}
function statusClass(status: string): string {
  if (status === "succeeded") return "succeeded";
  if (status === "partially_refunded") return "partially-refunded";
  if (status === "failed") return "failed";
  return "pending";
}

export default function PaymentHistoryPage(): React.ReactElement {
  const [items, setItems] = useState<SubscriptionPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filters, setFilters] = useState<BillingPaymentsFilterParams>({});
  const [supportPayment, setSupportPayment] =
    useState<SubscriptionPayment | null>(null);
  const [supportReason, setSupportReason] = useState("incorrect_amount");
  const [supportDescription, setSupportDescription] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState<string | null>(null);
  const [expandedRefundPayments, setExpandedRefundPayments] = useState<
    Set<number>
  >(new Set());

  const load = useCallback(async (nextFilters: BillingPaymentsFilterParams) => {
    setLoading(true);
    setError(null);
    try {
      setItems(
        (await fetchPaymentHistory({ limit: 100, ...nextFilters })).items,
      );
    } catch (err: any) {
      setItems([]);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load payment history",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filters);
  }, [filters, load]);
  const apply = (): void =>
    setFilters({
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
      ...(type ? { payment_type: type } : {}),
      ...(fromDate ? { date_from: fromDate } : {}),
      ...(toDate ? { date_to: toDate } : {}),
    });
  const clear = (): void => {
    setSearch("");
    setStatus("");
    setType("");
    setFromDate("");
    setToDate("");
    setFilters({});
  };
  const toggleRefundPayment = (paymentId: number): void =>
    setExpandedRefundPayments((current) => {
      const next = new Set(current);
      if (next.has(paymentId)) next.delete(paymentId);
      else next.add(paymentId);
      return next;
    });
  const refundParentIds = new Set(
    items
      .filter((item) => item.row_kind === "refund")
      .map((item) => item.parent_payment_id || item.id),
  );
  const visibleItems = items.filter(
    (item) =>
      !(
        item.status === "succeeded" &&
        item.row_kind !== "refund" &&
        refundParentIds.has(item.id)
      ),
  );
  const submitSupportTicket = async (): Promise<void> => {
    if (!supportPayment) return;
    setSupportSubmitting(true);
    try {
      const ticket = await createBillingSupportTicket({
        payment_id: supportPayment.id,
        reason: supportReason,
        description: supportDescription,
      });
      setSupportMessage(`Support ticket ${ticket.public_id} was submitted.`);
      setSupportPayment(null);
      setSupportDescription("");
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to submit the support ticket.",
      );
    } finally {
      setSupportSubmitting(false);
    }
  };

  return (
    <div className="sb-page">
      <div className="sb-header sb-payment-history-header">
        <div>
          <h1 className="sb-title">Payment History</h1>
          <p className="sb-subtitle">
            Review all billing transactions for your organization.
          </p>
        </div>
        <button
          className="sa-plans-btn sa-plans-btn-primary sb-payment-history-refresh"
          type="button"
          onClick={() => void load(filters)}
          disabled={loading}
          title="Refresh"
        >
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>
      <div className="sb-card">
        {supportMessage && (
          <div className="sb-inline-success">{supportMessage}</div>
        )}
        <div className="sb-filters">
          <div className="sb-filter-field sb-filter-search">
            <input
              className="sb-filter-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search amount, Stripe IDs, status..."
            />
          </div>
          <div className="sb-filter-field sb-filter-dropdown">
            <CustomDropdown
              value={status}
              options={STATUS_OPTIONS}
              onChange={setStatus}
              placeholder="All Statuses"
            />
          </div>
          <div className="sb-filter-field sb-filter-dropdown">
            <CustomDropdown
              value={type}
              options={TYPE_OPTIONS}
              onChange={setType}
              placeholder="All Types"
            />
          </div>
          <div className="sb-filter-field sb-filter-date">
            <input
              className="sb-filter-date-input"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>
          <div className="sb-filter-field sb-filter-date">
            <input
              className="sb-filter-date-input"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>
          <button
            className="sb-btn-search"
            type="button"
            onClick={apply}
            disabled={loading}
          >
            Search
          </button>
          {Object.keys(filters).length > 0 && (
            <button className="sb-btn-clear" type="button" onClick={clear}>
              Clear
            </button>
          )}
        </div>
        {error && <div className="sb-inline-error">{error}</div>}
        <div className="sb-table-wrap sb-table-scroll sb-payment-history-wrap">
          <table className="sb-table" data-responsive-cards="true">
            <thead>
              <tr>
                <th>Date</th>
                <th>For</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Period</th>
                <th>Receipt</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className="sb-empty-row">
                  <td colSpan={8}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading payments...
                  </td>
                </tr>
              ) : visibleItems.length === 0 ? (
                <tr className="sb-empty-row">
                  <td colSpan={8}>No payments yet</td>
                </tr>
              ) : (
                visibleItems.map((payment) => {
                  const isRefund =
                    payment.row_kind === "refund" ||
                    ["partially_refunded", "refunded"].includes(payment.status);
                  const parentPaymentId =
                    payment.parent_payment_id || payment.id;
                  const expanded =
                    isRefund && expandedRefundPayments.has(parentPaymentId);
                  const eligible =
                    !isRefund &&
                    ["succeeded", "partially_refunded"].includes(
                      payment.status,
                    ) &&
                    Math.max(
                      0,
                      payment.amount_cents -
                        (payment.refunded_amount_cents || 0),
                    ) > 0;
                  const originalPayment: SubscriptionPayment = {
                    ...payment,
                    row_kind: "related" as any,
                    status: "succeeded",
                    amount_cents:
                      payment.related_payment_amount_cents ||
                      payment.amount_cents,
                    paid_at:
                      payment.related_payment_paid_at ||
                      payment.related_payment_created_at ||
                      null,
                    created_at:
                      payment.related_payment_created_at || payment.created_at,
                    receipt_url: payment.related_payment_receipt_url || null,
                    period_start: payment.related_payment_period_start || null,
                    period_end: payment.related_payment_period_end || null,
                  };
                  const originalPaymentEligible =
                    isRefund &&
                    Math.max(
                      0,
                      originalPayment.amount_cents -
                        (originalPayment.refunded_amount_cents || 0),
                    ) > 0;
                  return (
                    <React.Fragment
                      key={`${payment.row_kind || "payment"}-${payment.id}-${payment.attempt_id || "legacy"}`}
                    >
                      <tr className={isRefund ? "sb-refund-group-row" : ""}>
                        <td data-label="Date">
                          {formatDate(payment.paid_at || payment.created_at)}
                        </td>
                        <td data-label="For">{formatFor(payment)}</td>
                        <td data-label="Type">{formatType(payment)}</td>
                        <td className="sb-td-amount" data-label="Amount">
                          <div>{formatUsd(payment.amount_cents)}</div>
                          {isRefund && (payment.refund_events || []).length > 1 && (
                            <div className="sb-refund-breakdown">
                              {payment.refund_events!.length} refunds
                            </div>
                          )}
                          {payment.status === "partially_refunded" &&
                            (payment.refunded_amount_cents || 0) > 0 && (
                              <div className="sb-refund-breakdown">
                                Refunded{" "}
                                {formatUsd(payment.refunded_amount_cents || 0)}{" "}
                                | Retained{" "}
                                {formatUsd(
                                  Math.max(
                                    0,
                                    payment.amount_cents -
                                      (payment.refunded_amount_cents || 0),
                                  ),
                                )}
                              </div>
                            )}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`sb-pay-status ${statusClass(payment.status)}`}
                          >
                            {formatStatus(payment.status)}
                          </span>
                        </td>
                        <td className="sb-td-muted" data-label="Period">
                          {payment.period_start
                            ? `${formatDate(payment.period_start)} - ${formatDate(payment.period_end)}`
                            : "-"}
                        </td>
                        <td data-label="Receipt">
                          {!isRefund &&
                          payment.status === "succeeded" &&
                          payment.receipt_url ? (
                            <button
                              type="button"
                              className="sb-payment-link"
                              onClick={() =>
                                window.open(
                                  payment.receipt_url!,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              View receipt
                            </button>
                          ) : (
                            <span className="sb-td-muted">-</span>
                          )}
                        </td>
                        <td data-label="Actions">
                          {isRefund ? (
                            <button
                              type="button"
                              className="sb-payment-history-expand"
                              onClick={() =>
                                toggleRefundPayment(parentPaymentId)
                              }
                              title={
                                expanded
                                  ? "Hide original payment"
                                  : "Show original payment"
                              }
                              aria-label={
                                expanded
                                  ? "Hide original payment"
                                  : "Show original payment"
                              }
                            >
                              {expanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          ) : eligible ? (
                            <button
                              type="button"
                              className="sb-report-issue-button"
                              onClick={() => setSupportPayment(payment)}
                            >
                              Report issue
                            </button>
                          ) : (
                            <span className="sb-td-muted">-</span>
                          )}
                        </td>
                      </tr>
                      {expanded && (
                        <>
                          {(payment.refund_events || []).length > 1 && (payment.refund_events || []).map((refundEvent) => (
                            <tr className="sb-related-payment-row" key={`refund-event-${refundEvent.id}`}>
                              <td data-label="Date">{formatDate(refundEvent.created_at)}</td>
                              <td data-label="For">{formatFor(payment)}</td>
                              <td data-label="Type">Refund</td>
                              <td className="sb-td-amount" data-label="Amount">{formatUsd(refundEvent.amount_cents)}</td>
                              <td data-label="Status"><span className="sb-pay-status partially-refunded">Refunded</span></td>
                              <td className="sb-td-muted" data-label="Period">-</td>
                              <td data-label="Receipt"><span className="sb-td-muted">-</span></td>
                              <td data-label="Actions"><span className="sb-td-muted">-</span></td>
                            </tr>
                          ))}
                        <tr className="sb-related-payment-row">
                          <td data-label="Date">
                            {formatDate(
                              originalPayment.paid_at ||
                                originalPayment.created_at,
                            )}
                          </td>
                          <td data-label="For">{formatFor(originalPayment)}</td>
                          <td data-label="Type">
                            {formatType(originalPayment)}
                          </td>
                          <td className="sb-td-amount" data-label="Amount">
                            {formatUsd(originalPayment.amount_cents)}
                          </td>
                          <td data-label="Status">
                            <span className="sb-pay-status succeeded">
                              Succeeded
                            </span>
                          </td>
                          <td className="sb-td-muted" data-label="Period">
                            {originalPayment.period_start
                              ? `${formatDate(originalPayment.period_start)} - ${formatDate(originalPayment.period_end)}`
                              : "-"}
                          </td>
                          <td data-label="Receipt">
                            {originalPayment.receipt_url ? (
                              <button
                                type="button"
                                className="sb-payment-link"
                                onClick={() =>
                                  window.open(
                                    originalPayment.receipt_url!,
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                              >
                                View receipt
                              </button>
                            ) : (
                              <span className="sb-td-muted">-</span>
                            )}
                          </td>
                          <td data-label="Actions">
                            {originalPaymentEligible ? (
                              <button
                                type="button"
                                className="sb-report-issue-button"
                                onClick={() => setSupportPayment(originalPayment)}
                              >
                                Report issue
                              </button>
                            ) : (
                              <span className="sb-td-muted">-</span>
                            )}
                          </td>
                        </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Modal
        show={supportPayment !== null}
        onHide={() => !supportSubmitting && setSupportPayment(null)}
        dialogClassName="sb-report-issue-modal"
        centered
        backdrop={supportSubmitting ? "static" : true}
        keyboard={!supportSubmitting}
      >
        <Modal.Header
          closeButton={!supportSubmitting}
          className="sb-report-issue-header"
        >
          <Modal.Title>Report a charge issue</Modal.Title>
        </Modal.Header>
        <Modal.Body className="sb-report-issue-body">
          <div className="sb-report-issue-charge">
            <span>Charge to review</span>
            <strong>
              {supportPayment ? formatUsd(supportPayment.amount_cents) : "—"}
            </strong>
            <small>
              {supportPayment?.payment_type?.replace(/_/g, " ") ||
                "Billing payment"}
            </small>
          </div>
          <div className="sb-report-issue-field">
            <span className="sb-form-label">Reason</span>
            <CustomDropdown
              value={supportReason}
              options={SUPPORT_TICKET_REASON_OPTIONS}
              onChange={setSupportReason}
              disabled={supportSubmitting}
            />
          </div>
          <div className="sb-report-issue-field">
            <label
              className="sb-form-label"
              htmlFor="payment-history-support-details"
            >
              Details
            </label>
            <textarea
              id="payment-history-support-details"
              className="sb-filter-input sb-report-issue-details"
              rows={4}
              value={supportDescription}
              onChange={(event) => setSupportDescription(event.target.value)}
              disabled={supportSubmitting}
              placeholder="Describe the issue and any details that can help us investigate."
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="sb-report-issue-footer">
          <button
            type="button"
            className="sb-btn-secondary"
            onClick={() => setSupportPayment(null)}
            disabled={supportSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sb-btn-primary"
            onClick={() => void submitSupportTicket()}
            disabled={
              supportSubmitting || supportDescription.trim().length < 10
            }
          >
            {supportSubmitting ? "Submitting..." : "Submit ticket"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
